import { type ReactNode } from 'react'

import { scopedTranslation } from '@utils/I18n'
import Button from '@atoms/Button'
import Icon from '@components/utils/Icon'
import { useDialog } from '@components/DialogProvider'
import useSupporterFilteringStore from './stores/useSupporterFilteringStore'
import { useEventRsvpOptions } from './useEventRsvpOptions'

const tActiveFilters = scopedTranslation('components.active_filters_bar')

function ChipRemoveButton({ onClick }) {
  return (
    <button onClick={onClick}>
      <Icon type="x-close" width={10} height={10} />
    </button>
  )
}

function Chip({ children }: { children: ReactNode }) {
  return (
    <div className="tw:inline-flex tw:items-center tw:py-3 tw:px-4 tw:rounded-lg tw:bg-primary-50 tw:text-sm tw:text-primary-700 tw:font-medium tw:gap-2">
      {children}
    </div>
  )
}

function valuePresenter(property: string, values, labelFor: (id: string) => string) {
  if (values === 'true' || values === 'false') {
    return ''
  }
  if (!Array.isArray(values)) return values
  if (property === 'event_rsvps') return values.map(labelFor).join(', ')

  return values.join(', ')
}

type ActiveFiltersBarProps = {
  groupId: string
  canExportGroupSupporters: boolean
  matchingCount: number
  totalCount: number
}

function ActiveFiltersBar({ groupId, canExportGroupSupporters, matchingCount, totalCount }: ActiveFiltersBarProps) {
  const { open: openDialog } = useDialog()
  const removeFilters = useSupporterFilteringStore((state) => state.removeFilters)
  const clearFilters = useSupporterFilteringStore((state) => () => {
    state.replaceFilters([])
  })
  const activeFilters = useSupporterFilteringStore((state) => state.filters)
  const nameSearch = useSupporterFilteringStore((state) => state.supporterName)
  const sortBy = useSupporterFilteringStore((state) => state.currentSort)
  // The bar is on every group page; only fetch event names when a chip has ids to
  // label. The presence predicates carry no ids, so they never need the list.
  const needsEventNames = (activeFilters ?? []).some(
    (filter) => filter.property === 'event_rsvps' && Array.isArray(filter.values)
  )
  const { optionLabel } = useEventRsvpOptions({ enabled: needsEventNames })

  if (!activeFilters || activeFilters.length === 0) return null

  return (
    <div className="tw:flex tw:justify-between tw:gap-x-6 tw:py-4 tw:border-t tw:border-gray-300">
      <div className="tw:flex tw:h-fit tw:flex-auto tw:justify-start tw:flex-wrap tw:gap-2">
        <h1 className="tw:sr-only">{tActiveFilters('title')}</h1>
        {activeFilters.map((filter, index) => {
          const { property, predicate, values } = filter
          const value = valuePresenter(property, values, optionLabel)
          return (
            <Chip key={`${property}.${predicate}-${index}`}>
              {tActiveFilters(`filter_searches.${property}.${predicate}`, {value})}
              <ChipRemoveButton
                onClick={() => {
                  removeFilters([filter])
                }}
              />
            </Chip>
          )
        })}
        <div></div>
      </div>
      <div className="tw:flex tw:flex-auto tw:w-min tw:justify-end tw:flex-wrap tw:gap-2">
        {canExportGroupSupporters && matchingCount > 0 && (
          <Button
            rank="link"
            leadingIcon="download-cloud"
            ariaLabel={tActiveFilters('aria_labels.export')}
            onClick={() =>
              openDialog({
                type: 'supporterExport',
                dialogData: {
                  groupId,
                  matchingCount,
                  totalCount,
                  sortBy,
                  nameSearch,
                  filters: activeFilters,
                },
              })
            }
          >
            {tActiveFilters('buttons.export')}
          </Button>
        )}
        <Button
          leadingIcon="refresh"
          onClick={() => {
            clearFilters()
          }}
        >
          {tActiveFilters('buttons.clear_filters')}
        </Button>
      </div>
    </div>
  )
}

export default ActiveFiltersBar
